import React, { useEffect, useMemo, useState } from 'react';
import { FaPlus, FaTimes, FaTrash } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import {
  addAgendaRow,
  applyMinutesChange,
  createDefaultAgendaRows,
  DEFAULT_AGENDA_MINUTES,
  getAgendaValidation,
  MAX_AGENDA_MINUTES_TOTAL,
} from '../utils/meetingAgenda';

const FieldErrorHint = ({ show, message = 'Required' }) => {
  if (!show) return null;
  return (
    <span
      className="absolute left-0 top-full mt-1 z-10 px-2 py-0.5 text-[11px] font-medium leading-tight text-red-700 bg-white border border-red-300 rounded shadow-md whitespace-nowrap dark:bg-gray-900 dark:text-red-400 dark:border-red-600"
      role="alert"
    >
      {message}
    </span>
  );
};

/**
 * @param {{
 *   isOpen: boolean;
 *   onClose: () => void;
 *   onConfirm: (rows: { minutes: number, focus: string }[]) => void;
 *   initialRows?: { minutes: number, focus: string }[];
 *   requireFocus?: boolean;
 *   variant?: 'roadmap' | 'department';
 * }} props
 */
const MeetingAgendaModal = ({
  isOpen,
  onClose,
  onConfirm,
  initialRows,
  requireFocus = true,
  variant = 'department',
}) => {
  const { darkMode } = useTheme();
  const [rows, setRows] = useState(() => createDefaultAgendaRows());
  const [showFieldErrors, setShowFieldErrors] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setShowFieldErrors(false);
    setRows(
      initialRows?.length
        ? initialRows.map((r) => ({
            minutes: Number(r.minutes) || DEFAULT_AGENDA_MINUTES,
            focus: r.focus ?? '',
          }))
        : createDefaultAgendaRows()
    );
  }, [isOpen, initialRows]);

  const validation = useMemo(
    () => getAgendaValidation(rows, { requireFocus }),
    [rows, requireFocus]
  );

  const handleMinutesChange = (index, value) => {
    setShowFieldErrors(false);
    setRows((prev) => applyMinutesChange(prev, index, value));
  };

  const handleFocusChange = (index, value) => {
    setShowFieldErrors(false);
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, focus: value } : row)));
  };

  const handleAddRow = () => {
    setShowFieldErrors(false);
    setRows((prev) => addAgendaRow(prev));
  };

  const handleRemoveRow = (index) => {
    setShowFieldErrors(false);
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      const removed = prev.filter((_, i) => i !== index);
      if (removed.length === 1) {
        return [{ ...removed[0], minutes: MAX_AGENDA_MINUTES_TOTAL }];
      }
      return removed;
    });
  };

  const submitAgenda = () => {
    onConfirm(
      rows.map((row) => ({
        minutes: Math.max(1, Math.round(Number(row.minutes) || 1)),
        focus: String(row.focus || '').trim(),
      }))
    );
  };

  const handleScheduleNow = (e) => {
    e.preventDefault();
    if (!validation.valid) {
      setShowFieldErrors(true);
      return;
    }
    submitAgenda();
  };

  if (!isOpen) return null;

  const subtitle =
    variant === 'roadmap'
      ? 'We pre-filled this from your current roadmap focus. Adjust time and topics before scheduling.'
      : 'What do you want to focus on?';

  const baseInputClass = `w-full px-3 py-2 rounded-lg border text-sm ${
    darkMode
      ? 'bg-gray-700 text-white placeholder-gray-400'
      : 'bg-white text-gray-900 placeholder-gray-500'
  }`;

  const inputBorder = (hasError) =>
    hasError
      ? 'border-red-500 ring-1 ring-red-500'
      : darkMode
        ? 'border-gray-600'
        : 'border-gray-300';

  const focusIndicesWithError = showFieldErrors ? new Set(validation.emptyFocusIndices) : new Set();
  const minutesIndicesWithError = showFieldErrors
    ? new Set([...validation.invalidMinutesIndices, ...(validation.totalExceeds ? rows.map((_, i) => i) : [])])
    : new Set();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="meeting-agenda-title"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          aria-label="Close"
        >
          <FaTimes className="w-5 h-5" />
        </button>

        <form onSubmit={handleScheduleNow} className="p-6">
          <h2
            id="meeting-agenda-title"
            className={`text-xl font-bold text-center pr-8 mb-2 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}
          >
            Meeting Agenda
          </h2>
          <p
            className={`text-sm text-center mb-6 ${
              darkMode ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            {subtitle}
          </p>

          <div className="space-y-3">
            {rows.map((row, index) => {
              const otherTotal = rows.reduce(
                (sum, r, i) => (i === index ? sum : sum + (Number(r.minutes) || 0)),
                0
              );
              const maxMinutesForRow = Math.max(1, MAX_AGENDA_MINUTES_TOTAL - otherTotal);
              const focusError = focusIndicesWithError.has(index);
              const minutesError = minutesIndicesWithError.has(index);

              return (
                <div
                  key={index}
                  className={`p-4 rounded-lg border ${
                    darkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-24 shrink-0">
                      <label
                        htmlFor={`agenda-minutes-${index}`}
                        className={`block text-xs font-medium mb-1 ${
                          darkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}
                      >
                        Minutes
                      </label>
                      <div className="relative">
                        <input
                          id={`agenda-minutes-${index}`}
                          type="number"
                          min={1}
                          max={maxMinutesForRow}
                          value={row.minutes}
                          onChange={(e) => handleMinutesChange(index, e.target.value)}
                          className={`${baseInputClass} ${inputBorder(minutesError)}`}
                          aria-label={`Minutes for focus ${index + 1}`}
                          aria-invalid={minutesError}
                          aria-describedby={
                            minutesError ? `agenda-minutes-error-${index}` : undefined
                          }
                        />
                        <FieldErrorHint
                          show={minutesError}
                          message={
                            validation.totalExceeds ? '60 min max' : 'Required'
                          }
                        />
                        {minutesError && (
                          <span id={`agenda-minutes-error-${index}`} className="sr-only">
                            {validation.totalExceeds ? '60 min max' : 'Required'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <label
                        htmlFor={`agenda-focus-${index}`}
                        className={`block text-xs font-medium mb-1 ${
                          darkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}
                      >
                        What to focus on
                        {requireFocus && (
                          <span className="text-red-600 dark:text-red-400 ml-0.5" aria-hidden="true">
                            *
                          </span>
                        )}
                      </label>
                      <div className="relative">
                        <input
                          id={`agenda-focus-${index}`}
                          type="text"
                          value={row.focus}
                          onChange={(e) => handleFocusChange(index, e.target.value)}
                          className={`${baseInputClass} ${inputBorder(focusError)}`}
                          placeholder={
                            variant === 'department'
                              ? 'e.g. Review Q1 pipeline, referral program setup…'
                              : 'e.g. Referral Program'
                          }
                          aria-label={`Focus topic ${index + 1}`}
                          aria-invalid={focusError}
                          aria-required={requireFocus}
                          aria-describedby={
                            focusError ? `agenda-focus-error-${index}` : undefined
                          }
                        />
                        <FieldErrorHint show={focusError} />
                        {focusError && (
                          <span id={`agenda-focus-error-${index}`} className="sr-only">
                            Required
                          </span>
                        )}
                      </div>
                    </div>
                    {rows.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(index)}
                        className={`mt-6 p-2 rounded-lg transition-colors ${
                          darkMode
                            ? 'text-gray-400 hover:bg-gray-600 hover:text-red-400'
                            : 'text-gray-500 hover:bg-gray-200 hover:text-red-600'
                        }`}
                        aria-label={`Remove focus ${index + 1}`}
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={handleAddRow}
            className={`mt-4 flex items-center gap-2 text-sm font-medium ${
              darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'
            }`}
          >
            <FaPlus className="w-3 h-3" />
            Add another focus
          </button>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                darkMode
                  ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-2 rounded-lg text-sm font-medium text-white ${
                validation.valid
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-red-600/50 cursor-not-allowed hover:bg-red-600/60'
              }`}
              aria-disabled={!validation.valid}
            >
              Schedule Now
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MeetingAgendaModal;
